import * as React from 'react';
import ConfirmPopup from './ConfirmPopup';
import { toast } from 'react-toastify';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';
import { useNavigate } from 'react-router-dom';

interface PriceListPowerPoleItem {
    id: number;
    distributor: string;
    singlePrice: number;
    threePhasePrice: number;
    statusId: number;
    isDirty: boolean;
}

const AdminPriceListPowerPoleConnectionData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [powerPoleConnectionItems, setPowerPoleConnectionItems] = React.useState<PriceListPowerPoleItem[]>([]);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const [deleteIndex, setDeleteIndex] = React.useState(-1);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("AdminPricelistsView");
        if (hasPermission) {
            getPriceListPowerPoleConnectionData();
        } else {
            navigate("/accessdenied");
        }
    }

    const getPriceListPowerPoleConnectionData = async () => {
        const apiUrl = "GetPriceListPowerPoleConnections";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            let ppConnections = result.data;
            let newBlankRow = addBlankRow();
            ppConnections.push(newBlankRow);

            setPowerPoleConnectionItems(ppConnections);
            setLoading(false);
        }
    }

    const addBlankRow = () => {
        let powerPoleItem: PriceListPowerPoleItem = {
            id: 0,
            distributor: "",
            singlePrice: 0,
            threePhasePrice: 0,
            statusId: 1,
            isDirty: false
        };

        return powerPoleItem;
    }

    const clearError = (errorItem: string) => {
        let errorList = errors;
        delete errorList[errorItem];

        setErrors(errorList);
    }

    const handleCellChangeText = (e: any, itemIndex: number) => {
        clearError("pricelistitems");

        setPowerPoleConnectionItems(prevState => prevState.map((item, index) =>
            index == itemIndex ? { ...item, [e.target.name]: e.target.value, isDirty: true } : item
        ));

        if (itemIndex == (powerPoleConnectionItems.length - 1)) {
            //add new row
            var newItem = addBlankRow();
            setPowerPoleConnectionItems(prevList => [...prevList, newItem]);
        }
    }

    const handleCellChangeNumber = (e: any, itemIndex: number) => {
        clearError("pricelistitems");

        setPowerPoleConnectionItems(prevState => prevState.map((item, index) =>
            index == itemIndex ? { ...item, [e.target.name]: Number(e.target.value), isDirty: true } : item
        ));

        if (itemIndex == (powerPoleConnectionItems.length - 1)) {
            //add new row
            var newItem = addBlankRow();
            setPowerPoleConnectionItems(prevList => [...prevList, newItem]);
        }
    }

    //DELETE PRICELIST ITEM
    const deleteItem = (e: any, itemIndex: number) => {
        e.preventDefault();

        setShowDeleteConfirm(true);
        setDeleteIndex(itemIndex);
    }

    const hideDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        setDeleteIndex(-1);
    }

    const saveDeleteConfirm = () => {
        var pricelist = [...powerPoleConnectionItems];
        var pricelistItem = pricelist[deleteIndex];

        if (pricelistItem.id === 0) {
            //just remove from the list as not saved
            pricelist.splice(deleteIndex, 1);
            setShowDeleteConfirm(false);
            setDeleteIndex(-1);
            setPowerPoleConnectionItems(pricelist);

            toast.success("Item deleted successfully");
        } else {
            saveDeletePricelist(pricelist, pricelistItem.id);
        }
    }

    const saveDeletePricelist = async (pricelist: PriceListPowerPoleItem[], powerPoleId: number) => {
        var deletePriceList = {
            id: powerPoleId
        };

        var jsonItem = JSON.stringify(deletePriceList);

        const apiUrl = "DeletePriceListPowerPoleConnection";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Power Pole Connection Deleted Successfully");
                pricelist.splice(deleteIndex, 1);
                setShowDeleteConfirm(false);
                setDeleteIndex(-1);
                setPowerPoleConnectionItems(pricelist);
            }
        }
    }
    //END DELETE PRICELIST ITEM

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;
        let items = powerPoleConnectionItems;

        if (powerPoleConnectionItems.length === 1) {
            errorList["pricelistitems"] = "Please enter at least one item!";
            setErrors(errorList)
            return true;
        }

        var rowNumber = 0;
        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < items.length - 1; i++) {
            rowNumber += 1;
            var newErrorString = "";

            if (!items[i].distributor || items[i].distributor === "") {
                newErrorString += "Distributor, ";
                error = true;
            }

            if (items[i].singlePrice <= 0) {
                newErrorString += "Single Phase Price, ";
                error = true;
            }

            if (items[i].threePhasePrice <= 0) {
                newErrorString += "3 Phase Price, ";
                error = true;
            }

            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }

        }
        if (error) {
            formIsValid = false;
            errorList["pricelistitems"] = errorString;
        }
        setErrors(errorList);
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            var changed = powerPoleConnectionItems.filter(p => p.isDirty);

            if (changed.length === 0) {
                toast.info("No items have changed.");
            } else {
                savePricelist(changed);
            }
        } else {
            toast.error("Please fix validation errors before Saving")
        }
    }

    const savePricelist = async (changed: PriceListPowerPoleItem[]) => {
        var savePriceList = {
            items: changed
        };

        var jsonItem = JSON.stringify(savePriceList);

        const apiUrl = "SavePriceListPowerPoleConnections";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Power Pole Connections Saved Successfully");
                setLoading(true);
                getPriceListPowerPoleConnectionData();
            }
        }
    }

    const lastRow = powerPoleConnectionItems.length - 1;
    const confirmPopup = <ConfirmPopup heading="Delete Power Pole Item?" text="Are you sure you want to delete this power pole connection item?" hideConfirmModal={hideDeleteConfirm} showConfirmModal={showDeleteConfirm} saveConfirmModal={saveDeleteConfirm} />

    const renderDetails = (
        <div>
            <div className="static-modal">
                {confirmPopup}
            </div>
            <div className="overflowAuto">
                <table className="table table--nowidth tableColours">
                    <thead>
                        <tr>
                            <th className="hidden">Id</th>
                            <th></th>
                            <th>Distributor</th>
                            <th>Single Price</th>
                            <th>3 Phase Price</th>
                            <th></th>
                        </tr>
                    </thead>
                    {powerPoleConnectionItems.map((item, index) =>
                        <tbody key={index}>
                            <tr>
                                <td className="hidden">{item.id}</td>
                                <td className="table__text--align">{index + 1}</td>
                                <td className="table__text--align">
                                    <input type='text' maxLength={50} className='form-control' id="distributor" name="distributor" value={item.distributor} onChange={(e) => handleCellChangeText(e, index)} />
                                </td>
                                <td className="table__text--align">
                                    <input type='number' min="0" step="0.01" className='form-control textalignright' id="singlePrice" name="singlePrice" value={item.singlePrice} onChange={(e) => handleCellChangeNumber(e, index)} />
                                </td>
                                <td className="table__text--align">
                                    <input type='number' min="0" step="0.01" className='form-control textalignright' id="threePhasePrice" name="threePhasePrice" value={item.threePhasePrice} onChange={(e) => handleCellChangeNumber(e, index)} />
                                </td>
                                <td className="table__text--align">
                                    <div className={index === lastRow ? "hidden" : "delete--tablecell"}>
                                        <a className="makeitred" href="#" onClick={(e) => deleteItem(e, index)}>
                                            <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                        </a>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>
                {errors["pricelistitems"] ?
                    (errors["pricelistitems"]).split("\n").map((item: any, key: any) => {
                        return <span className="label errors" key={key}>{item}<br /></span>
                    })
                    : ""}
            </div>
            <div className="marginBottom10">
                <button className="defaultbutton defaultbutton__large" type="button" onClick={save} disabled={loading ? true : false}>Save</button>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            <h1>Power Pole Connection Prices</h1>
            {contents}
        </div>
    );
}

export default AdminPriceListPowerPoleConnectionData;